'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { format } from 'date-fns';
import { Loader2, Clock, Users } from 'lucide-react';
import { getTimeSlots } from '@/client/sdk.gen';
import { TimeSlotItem } from '@/client/types.gen';
import { Badge } from '@/components/ui/badge';

interface TimeSlotsProps {
  date: Date;
  onSelectTime: (time: string, endTime: string) => void;
  selectedTime: string | null;
  username: string;
  eventTypeSlug: string;
  timezone?: string;
}

export function TimeSlots({
  date,
  onSelectTime,
  selectedTime,
  username,
  eventTypeSlug,
  timezone,
}: TimeSlotsProps) {
  const [availableSlots, setAvailableSlots] = useState<TimeSlotItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when date changes
    setIsLoading(true);
    setError(null);
    setAvailableSlots([]);

    const fetchTimeSlots = async () => {
      try {
        console.log('Fetching time slots for:', {
          username,
          eventTypeSlug,
          date,
          timezone,
        });

        const response = await getTimeSlots({
          path: {
            username,
            event_type_slug: eventTypeSlug,
          },
          query: {
            year: date.getFullYear(),
            month: date.getMonth() + 1, // JavaScript months are 0-indexed
            day: date.getDate(), // Add the required day parameter
            timezone:
              timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });

        console.log('Time slots API response:', response);

        if (response.error) {
          throw new Error(`Failed to fetch time slots: ${response.error}`);
        }

        if (response.data) {
          // API now returns time slots directly for the requested day
          setAvailableSlots(response.data.time_slots || []);
        }
      } catch (err) {
        console.error('Error fetching time slots:', err);
        setError('Failed to load available times. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeSlots();
  }, [date, username, eventTypeSlug, timezone]);

  if (isLoading) {
    return (
      <Card className="overflow-hidden border shadow-md">
        <div className="p-4 border-b bg-muted">
          <h2 className="text-lg font-semibold text-foreground">
            Available Times
          </h2>
        </div>
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
            <p className="text-muted-foreground text-center">
              Loading available times...
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 max-w-lg mx-auto">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-10 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="overflow-hidden border shadow-md">
        <div className="p-4 border-b bg-destructive/10">
          <h2 className="text-lg font-semibold text-destructive">Error</h2>
        </div>
        <CardContent className="p-6">
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <Card className="overflow-hidden border shadow-md">
        <div className="p-4 border-b bg-muted">
          <h2 className="text-lg font-semibold text-foreground">
            Available Times
          </h2>
        </div>
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Clock className="h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2 text-foreground text-center">
              No available times on {format(date, 'EEEE, MMMM d')}
            </h3>
            <p className="text-muted-foreground max-w-md text-center">
              Please select another date from the calendar to see available time
              slots.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border shadow-md">
      <div className="p-4 border-b bg-muted">
        <h2 className="text-lg font-semibold text-foreground">
          Available Times
        </h2>
      </div>
      <CardContent className="p-6">
        <h3 className="text-base font-medium mb-4 text-foreground text-center">
          Select a time on {format(date, 'EEEE, MMMM d')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-md mx-auto">
          {availableSlots.map((slot) => {
            // Format the time for display
            const startTime = new Date(slot.start_time);
            const formattedTime = format(startTime, 'h:mm a');
            const hasLimitedSlots = typeof slot.slots_left === 'number';

            return (
              <div key={slot.start_time} className="flex flex-col gap-1">
                <Button
                  variant={
                    selectedTime === slot.start_time ? 'default' : 'outline'
                  }
                  className="h-12 justify-center"
                  onClick={() => onSelectTime(slot.start_time, slot.end_time)}
                >
                  {formattedTime}
                </Button>
                {hasLimitedSlots && (
                  <div className="flex items-center justify-center text-xs text-muted-foreground">
                    <Users className="h-3 w-3 mr-1" />
                    <span>{slot.slots_left} spots left</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
